import { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Upload, Music, FileAudio, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { rawFetch } from '../../services/api';

interface AssetUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  slug: string;
  onUploadComplete: (asset: AssetData) => void;
}

export interface AssetData {
  id: string;
  trackReference: string | null;
  title: string;
  coverArtUrl: string | null;
  verificationStatus?: string | null;
}

interface UploadState {
  status: 'form' | 'validating' | 'uploading-wav' | 'confirming' | 'complete' | 'error';
  progress: number;
  error: string | null;
  step: string;
}

const MAX_WAV_FILE_SIZE = 500 * 1024 * 1024; // 500MB

export default function AssetUploadModal({ isOpen, onClose, slug, onUploadComplete }: AssetUploadModalProps) {
  const [title, setTitle] = useState('');
  const [wavFile, setWavFile] = useState<File | null>(null);
  const [releasedToStreamingEnabled, setReleasedToStreamingEnabled] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [uploadState, setUploadState] = useState<UploadState>({
    status: 'form',
    progress: 0,
    error: null,
    step: '',
  });

  const wavRef = useRef<HTMLInputElement>(null);
  const isSubmittingRef = useRef(false);

  const handleWavChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.wav')) {
      setFormErrors((prev) => ({ ...prev, wav: 'Must be a .wav file' }));
      return;
    }

    if (file.size > MAX_WAV_FILE_SIZE) {
      setFormErrors((prev) => ({ ...prev, wav: 'WAV file must be under 500MB' }));
      return;
    }

    setFormErrors((prev) => { const n = { ...prev }; delete n.wav; return n; });
    setWavFile(file);
  };

  const resetForm = () => {
    setTitle('');
    setWavFile(null);
    setReleasedToStreamingEnabled(false);
    setFormErrors({});
    setUploadState({ status: 'form', progress: 0, error: null, step: '' });
    isSubmittingRef.current = false;
  };

  const handleClose = useCallback(() => {
    if (uploadState.status === 'uploading-wav' || uploadState.status === 'confirming') {
      return; // prevent closing during active upload
    }
    resetForm();
    onClose();
  }, [uploadState.status, onClose]);

  const uploadBlob = (url: string, blob: Blob | File, contentType: string, onProgress: (pct: number) => void): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Upload failed with status ${xhr.status}`));
      };
      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.send(blob);
    });
  };

  const handleSubmit = async () => {
    // Guard against double-click / concurrent submissions
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    // Form validation
    const newErrors: Record<string, string> = {};
    if (!title.trim()) newErrors.title = 'Track title is required';
    if (!wavFile) newErrors.wav = 'WAV file is required';

    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      isSubmittingRef.current = false;
      return;
    }

    // Start upload process
    setUploadState({ status: 'validating', progress: 0, error: null, step: 'Validating files...' });

    // Get presigned URLs from backend
    let presignData: {
      trackId: string;
      wavUploadUrl: string;
      wavPath: string;
      aacUploadUrl: string;
      aacPath: string;
    };

    try {
      const res = await rawFetch('/api/public/tenants/tracks/presign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug },
        body: JSON.stringify({
          title: title.trim(),
          hasCoverArt: false,
          coverContentType: null,
          coverFileSize: 0,
          wavFileSize: wavFile!.size,
        }),
      });

      if (!res.ok) {
        const errData = await res.json() as { error: string };
        setUploadState({ status: 'error', progress: 0, error: errData.error || 'Failed to prepare upload', step: '' });
        isSubmittingRef.current = false;
        return;
      }

      presignData = await res.json() as typeof presignData;
    } catch {
      setUploadState({ status: 'error', progress: 0, error: 'Network error. Please check your connection.', step: '' });
      isSubmittingRef.current = false;
      return;
    }

    // Upload WAV file
    setUploadState({ status: 'uploading-wav', progress: 5, error: null, step: 'Uploading WAV file...' });

    try {
      await uploadBlob(presignData.wavUploadUrl, wavFile!, 'audio/wav', (pct) => {
        setUploadState((prev) => ({
          ...prev,
          progress: Math.round(5 + (pct / 100) * 70),
        }));
      });
    } catch {
      setUploadState({ status: 'error', progress: 0, error: 'WAV upload failed. Please try again.', step: '' });
      isSubmittingRef.current = false;
      return;
    }

    // Confirm with backend (transcoding is handled by backend worker)
    setUploadState({ status: 'confirming', progress: 85, error: null, step: 'Finalising track...' });

    try {
      const confirmRes = await rawFetch('/api/public/tenants/tracks/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug },
        body: JSON.stringify({
          trackId: presignData.trackId,
          title: title.trim(),
          coverPath: null,
          wavPath: presignData.wavPath,
          aacPath: null, // AAC transcoding handled by backend worker
          releasedToStreamingEnabled,
        }),
      });

      if (!confirmRes.ok) {
        const errData = await confirmRes.json() as { error: string };
        setUploadState({ status: 'error', progress: 0, error: errData.error || 'Backend rejected the track.', step: '' });
        isSubmittingRef.current = false;
        return;
      }

      const confirmData = await confirmRes.json() as { trackId: string; trackReference?: string; coverArtUrl: string | null };

      setUploadState({ status: 'complete', progress: 100, error: null, step: 'Track uploaded!' });

      setTimeout(() => {
        onUploadComplete({
          id: confirmData.trackId || presignData.trackId,
          trackReference: confirmData.trackReference || null,
          title: title.trim(),
          coverArtUrl: null,
          verificationStatus: 'PROCESSING',
        });
        resetForm();
        onClose();
      }, 1500);
    } catch {
      setUploadState({ status: 'error', progress: 0, error: 'Failed to confirm track. Please try again.', step: '' });
      isSubmittingRef.current = false;
    }
  };

  const isUploading = ['validating', 'uploading-wav', 'confirming', 'complete'].includes(uploadState.status);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={handleClose} />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-lg bg-[#1A1A1A] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-white/5">
              <div className="flex items-center gap-2">
                <Music className="w-5 h-5 text-electric-cyan" />
                <h3 className="text-lg font-semibold text-white">Upload Track</h3>
              </div>
              <button
                onClick={handleClose}
                disabled={isUploading}
                className="p-1.5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
              {/* Error message (upload phase) */}
              <AnimatePresence>
                {uploadState.error && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-300">{uploadState.error}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Form or progress */}
              {!isUploading ? (
                <>
                  {/* Track Title */}
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1.5">
                      Track Title *
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Enter track title"
                      className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-text-muted text-sm focus:outline-none focus:border-electric-cyan/50 transition-colors"
                    />
                    {formErrors.title && <p className="text-xs text-red-400 mt-1">{formErrors.title}</p>}
                  </div>

                  {/* WAV File */}
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1.5">
                      WAV File *
                    </label>
                    <div
                      onClick={() => wavRef.current?.click()}
                      className="flex items-center gap-3 px-4 py-3 bg-white/5 border border-white/10 rounded-lg cursor-pointer hover:border-white/20 transition-colors"
                    >
                      <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center">
                        <FileAudio className="w-5 h-5 text-text-muted" />
                      </div>
                      <span className="text-sm text-text-muted truncate">
                        {wavFile ? wavFile.name : 'Choose WAV audio file...'}
                      </span>
                    </div>
                    <input
                      ref={wavRef}
                      type="file"
                      accept=".wav,audio/wav"
                      className="hidden"
                      onChange={handleWavChange}
                    />
                    {formErrors.wav && <p className="text-xs text-red-400 mt-1">{formErrors.wav}</p>}
                  </div>

                  <div className="rounded-lg border border-white/10 bg-white/5 p-3 space-y-2">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={releasedToStreamingEnabled}
                        onChange={(e) => setReleasedToStreamingEnabled(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-white/20 bg-transparent text-electric-cyan focus:ring-electric-cyan/40"
                      />
                      <span className="text-sm text-white">
                        This track has already released to streaming services.
                      </span>
                    </label>
                    <p className="text-xs text-text-secondary leading-relaxed">
                      <strong>Important:</strong> Upload the same final WAV master you supplied to your distributor. A different mix, master or edit may not match the released recording. If you intend to release this later, you can request that the track be connected once released through your distributor.
                    </p>
                  </div>
                </>
              ) : (
                /* Upload progress */
                <div className="space-y-4 py-4">
                  <div className="text-center">
                    <p className="text-sm font-medium text-white mb-1">{title}</p>
                  </div>

                  {/* Progress */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-text-secondary">{uploadState.step}</span>
                      <span className="text-sm font-mono text-electric-cyan">{uploadState.progress}%</span>
                    </div>

                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                      <motion.div
                        className={`h-full rounded-full ${
                          uploadState.status === 'complete' ? 'bg-green-500' : 'bg-electric-cyan'
                        }`}
                        initial={{ width: 0 }}
                        animate={{ width: `${uploadState.progress}%` }}
                        transition={{ duration: 0.3, ease: 'easeOut' }}
                      />
                    </div>

                    {uploadState.status === 'complete' && (
                      <motion.div
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center justify-center gap-2 text-sm text-green-400"
                      >
                        <CheckCircle className="w-4 h-4" />
                        <span>Track uploaded.</span>
                      </motion.div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            {!isUploading && (
              <div className="flex items-center justify-end gap-3 p-5 border-t border-white/5">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-text-muted hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-electric-cyan text-deep-charcoal text-sm font-semibold hover:bg-electric-cyan/90 transition-colors cursor-pointer"
                >
                  <Upload className="w-4 h-4" />
                  Upload Track
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
